// src/pages/ventes/DetailDevis.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Card, Row, Col, Button, Badge, Table, Spinner, Alert,
    Modal, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate, formatNumber } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";
import logo from "../assets/img/IMG-20250130-WA0008.jpg";
import html2pdf from 'html2pdf.js';

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 3000 }) => {
    if (!show) return null;

    const toastConfig = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    };
    const config = toastConfig[type] || toastConfig.info;

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom">
                    <p className="mb-0">{message}</p>
                </div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function DetailDevis() {
    const navigate = useNavigate();
    const { id } = useParams();
    const printRef = useRef();

    const [devis, setDevis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showConvertModal, setShowConvertModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [generatingPDF, setGeneratingPDF] = useState(false);
    const [societeInfos, setSocieteInfos] = useState(null);
    const [logoBase64, setLogoBase64] = useState('');
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });

    const [taxes, setTaxes] = useState({
        tva_taux: 18,
        css_taux: 1,
        tva_active: true,
        css_active: true
    });

    useEffect(() => {
        const loadAllData = async () => {
            try {
                setLoading(true);
                await Promise.all([
                    fetchDevis(),
                    fetchTaxes(),
                    fetchSocieteInfos(),
                    loadLogoBase64()
                ]);
            } catch (error) {
                console.error("Erreur lors du chargement des données:", error);
            } finally {
                setLoading(false);
            }
        };
        loadAllData();
    }, [id]);

    /* ============================================================
       FETCH (logique conservée)
       ============================================================ */
    const fetchDevis = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/devis.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setDevis(result.data);
            return result.data;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) setTaxes(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };

    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=societe`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) setSocieteInfos(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const loadLogoBase64 = () => {
        return new Promise((resolve) => {
            try {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    const dataURL = canvas.toDataURL('image/jpeg');
                    setLogoBase64(dataURL);
                    resolve(dataURL);
                };
                img.onerror = () => { setLogoBase64(''); resolve(''); };
                img.src = logo;
            } catch (error) {
                setLogoBase64('');
                resolve('');
            }
        });
    };

    const company = {
        name: societeInfos?.nom || 'GEOTECH',
        address: societeInfos?.adresse || 'Libreville, Gabon',
        phone: societeInfos?.telephone || '+241 00 00 00 00',
        email: societeInfos?.email || 'contact@geotech.com',
        site_web: societeInfos?.site_web || 'www.geotech.com',
        rc: societeInfos?.rc || 'RC123456',
        nif: societeInfos?.nif || 'NIF123456',
        patente: societeInfos?.patente || 'PAT123456'
    };

    const showToast = (type, title, message) => {
        setToast({ show: true, type, title, message });
    };
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });

    const handleConvertirEnFacture = async () => {
        setSaving(true);
        try {
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            const token = localStorage.getItem("token");

            const response = await fetch(`${API_BASE_URL}/devis.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'convertir',
                    id_devis: id,
                    id_utilisateur: user?.id
                })
            });

            const result = await response.json();

            if (result.success) {
                setShowConvertModal(false);
                fetchDevis();
                showToast('success', 'Devis converti', 'Devis converti en facture avec succès');
                setTimeout(() => navigate('/ventes/factures'), 2000);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la conversion");
            }
        } catch (err) {
            showToast('error', 'Erreur de connexion', "Impossible de contacter le serveur");
        } finally {
            setSaving(false);
        }
    };

    /* ============================================================
       IMPRESSION PROFESSIONNELLE — Design bordereau
       ============================================================ */
    const genererHTMLDevis = () => {
        if (!devis) return '';

        const statutConfig = {
            brouillon:        { class: 'draft',    label: 'BROUILLON' },
            envoye:           { class: 'sent',     label: 'ENVOYÉ' },
            accepte:          { class: 'accepted', label: 'ACCEPTÉ' },
            refuse:           { class: 'refused',  label: 'REFUSÉ' },
            converti_facture: { class: 'converted',label: 'CONVERTI EN FACTURE' },
            expire:           { class: 'expired',  label: 'EXPIRÉ' }
        };

        const estExpirePrint = devis && new Date(devis.date_validite) < new Date();
        let statut = statutConfig[devis.statut] || statutConfig.brouillon;
        if (estExpirePrint && (devis.statut === 'envoye' || devis.statut === 'brouillon')) {
            statut = statutConfig.expire;
        }

        const totalHT = devis.montant_ht || 0;
        const totalTVA = devis.montant_tva || 0;
        const totalCSS = devis.montant_css || 0;
        const totalTTC = devis.net_a_payer || 0;
        const logoSrc = logoBase64;
        const logoText = company.name.substring(0, 2).toUpperCase();

        // Montant en lettres
        const nombreEnLettres = (n) => {
            const unites = ['zéro','un','deux','trois','quatre','cinq','six','sept','huit','neuf','dix','onze','douze','treize','quatorze','quinze','seize','dix-sept','dix-huit','dix-neuf'];
            const dizaines = ['','','vingt','trente','quarante','cinquante','soixante','soixante-dix','quatre-vingt','quatre-vingt-dix'];
            if (n < 20) return unites[n];
            if (n < 100) {
                const d = Math.floor(n / 10);
                const u = n % 10;
                return dizaines[d] + (u === 1 && d !== 8 ? '-et-un' : u ? '-' + unites[u] : '');
            }
            if (n < 1000) {
                const c = Math.floor(n / 100);
                const r = n % 100;
                return (c === 1 ? 'cent' : unites[c] + ' cent') + (r ? ' ' + nombreEnLettres(r) : '');
            }
            if (n < 1000000) {
                const m = Math.floor(n / 1000);
                const r = n % 1000;
                return (m === 1 ? 'mille' : nombreEnLettres(m) + ' mille') + (r ? ' ' + nombreEnLettres(r) : '');
            }
            const mi = Math.floor(n / 1000000);
            const r = n % 1000000;
            return (mi === 1 ? 'un million' : nombreEnLettres(mi) + ' millions') + (r ? ' ' + nombreEnLettres(r) : '');
        };
        const montantEnLettres = nombreEnLettres(Math.round(totalTTC)).toUpperCase();

        return `
            <!DOCTYPE html>
            <html lang="fr">
            <head>
                <meta charset="UTF-8">
                <title>Devis ${devis.numero_proforma}</title>
                <style>
                    @page { size: A4; margin: 12mm 10mm; }
                    * { margin: 0; padding: 0; box-sizing: border-box; }

                    body {
                        font-family: 'Helvetica Neue', 'Segoe UI', Arial, sans-serif;
                        background: white;
                        color: #1e293b;
                        font-size: 10.5px;
                        line-height: 1.5;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }

                    .invoice { max-width: 210mm; margin: 0 auto; background: white; position: relative; }

                    /* BANDEAU SUPÉRIEUR */
                    .top-band {
                        display: flex; justify-content: space-between; align-items: stretch;
                        border-radius: 8px 8px 0 0; overflow: hidden;
                        background: linear-gradient(135deg, #1a3c5e 0%, #295AA7 100%);
                        color: white;
                    }
                    .top-band-left {
                        padding: 22px 26px; display: flex; align-items: center; gap: 18px; flex: 1;
                    }
                    .logo-container {
                        width: 70px; height: 70px; background: white; border-radius: 8px;
                        display: flex; align-items: center; justify-content: center;
                        overflow: hidden; flex-shrink: 0;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    }
                    .logo-container img { max-width: 100%; max-height: 100%; object-fit: contain; }
                    .logo-fallback { font-size: 24px; font-weight: 800; color: #1a3c5e; letter-spacing: 1px; }
                    .company-header h1 {
                        font-size: 22px; font-weight: 800; letter-spacing: 2px;
                        margin-bottom: 4px; text-transform: uppercase;
                    }
                    .company-header .tagline {
                        font-size: 9px; letter-spacing: 3px; opacity: 0.75;
                        text-transform: uppercase; font-weight: 300;
                    }
                    .top-band-right {
                        padding: 22px 26px; text-align: right;
                        display: flex; flex-direction: column; justify-content: center;
                        min-width: 220px; background: #C42625;
                        border-left: 1px solid rgba(255,255,255,0.15);
                    }
                    .doc-type {
                        font-size: 26px; font-weight: 800; letter-spacing: 4px;
                        margin-bottom: 6px; text-transform: uppercase;
                    }
                    .doc-number {
                        font-size: 11px; font-family: 'Courier New', monospace;
                        font-weight: 600; letter-spacing: 1px; margin-bottom: 8px; opacity: 0.95;
                    }
                    .doc-date { font-size: 9px; opacity: 0.75; letter-spacing: 1px; }

                    /* STATUT */
                    .status-strip {
                        display: flex; justify-content: center; align-items: center;
                        padding: 8px 20px; font-size: 10px; font-weight: 700;
                        letter-spacing: 3px; text-transform: uppercase;
                        border-bottom: 3px solid;
                    }
                    .status-paid      { background: #ecfdf5; color: #065f46; border-color: #10b981; }
                    .status-sent      { background: #eff6ff; color: #1e40af; border-color: #3b82f6; }
                    .status-accepted  { background: #ecfdf5; color: #065f46; border-color: #10b981; }
                    .status-refused   { background: #fef2f2; color: #991b1b; border-color: #ef4444; }
                    .status-draft     { background: #f1f5f9; color: #475569; border-color: #94a3b8; }
                    .status-converted { background: #eef2ff; color: #3730a3; border-color: #6366f1; }
                    .status-expired   { background: #fff7ed; color: #9a3412; border-color: #f97316; }

                    /* PARTIES */
                    .parties {
                        display: flex; gap: 14px; padding: 20px 0;
                        border-bottom: 1px solid #e2e8f0;
                    }
                    .party {
                        flex: 1; padding: 14px 16px; background: #f8fafc;
                        border-radius: 8px; border-left: 3px solid #295AA7;
                    }
                    .party-client { border-left-color: #21ACCD; }
                    .party-label {
                        font-size: 8.5px; font-weight: 700; letter-spacing: 2px;
                        text-transform: uppercase; color: #295AA7;
                        margin-bottom: 8px; display: flex; align-items: center; gap: 6px;
                    }
                    .party-client .party-label { color: #21ACCD; }
                    .party-label::before {
                        content: ''; width: 6px; height: 6px;
                        border-radius: 50%; background: currentColor;
                    }
                    .party-name {
                        font-size: 13px; font-weight: 700;
                        color: #0f172a; margin-bottom: 6px;
                    }
                    .party-details { font-size: 9.5px; color: #475569; line-height: 1.7; }
                    .party-details .label {
                        display: inline-block; min-width: 60px;
                        color: #94a3b8; font-weight: 500;
                    }

                    /* MÉTADONNÉES */
                    .invoice-meta {
                        display: flex; justify-content: flex-end; gap: 10px;
                        padding: 14px 0; margin-bottom: 8px;
                    }
                    .meta-card {
                        min-width: 150px; padding: 8px 14px;
                        background: white; border: 1px solid #e2e8f0;
                        border-radius: 6px; text-align: center;
                    }
                    .meta-card .meta-label {
                        font-size: 8px; font-weight: 600; letter-spacing: 1.5px;
                        text-transform: uppercase; color: #94a3b8; margin-bottom: 3px;
                    }
                    .meta-card .meta-value {
                        font-size: 11px; font-weight: 700; color: #1e293b;
                    }
                    .meta-card.urgent .meta-value { color: #ef4444; }
                    .meta-card.validity {
                        background: linear-gradient(135deg, #fffbeb, #fef3c7);
                        border-color: #f59e0b;
                    }
                    .meta-card.validity .meta-label { color: #92400e; }
                    .meta-card.validity .meta-value { color: #92400e; }
                    .meta-card.validity.urgent {
                        background: linear-gradient(135deg, #fef2f2, #fee2e2);
                        border-color: #ef4444;
                    }
                    .meta-card.validity.urgent .meta-label,
                    .meta-card.validity.urgent .meta-value { color: #991b1b; }

                    /* NOTES */
                    .notes-box {
                        padding: 12px 16px; background: #fffbeb;
                        border-left: 3px solid #f59e0b;
                        border-radius: 6px; font-size: 9.5px;
                        color: #78350f; margin-bottom: 16px;
                    }
                    .notes-box strong { color: #92400e; }

                    /* TABLE */
                    .table-wrapper {
                        border-radius: 8px; overflow: hidden;
                        border: 1px solid #e2e8f0; margin-bottom: 16px;
                    }
                    table.lines { width: 100%; border-collapse: collapse; }
                    table.lines thead {
                        background: linear-gradient(135deg, #1a3c5e, #295AA7);
                        color: white;
                    }
                    table.lines thead th {
                        padding: 10px 12px; font-size: 9px;
                        font-weight: 700; letter-spacing: 1.2px;
                        text-transform: uppercase; text-align: left;
                    }
                    table.lines thead th.center { text-align: center; }
                    table.lines thead th.right  { text-align: right; }
                    table.lines tbody td {
                        padding: 10px 12px; font-size: 10px;
                        color: #334155; border-bottom: 1px solid #f1f5f9;
                        vertical-align: top;
                    }
                    table.lines tbody tr:nth-child(even) { background: #fafbfc; }
                    table.lines tbody tr:last-child td { border-bottom: none; }
                    table.lines td.center { text-align: center; }
                    table.lines td.right  { text-align: right; }
                    table.lines td.ref {
                        font-family: 'Courier New', monospace;
                        font-size: 9px; color: #64748b;
                    }
                    table.lines td.designation { font-weight: 600; color: #0f172a; }
                    table.lines td.total { font-weight: 700; color: #1a3c5e; }
                    .remise-tag {
                        display: inline-block; padding: 1px 6px;
                        background: #fef3c7; color: #92400e;
                        border-radius: 3px; font-size: 8.5px; font-weight: 600;
                    }

                    /* TOTAUX */
                    .totals-section {
                        display: flex; justify-content: space-between;
                        align-items: flex-start; gap: 20px; margin-bottom: 20px;
                    }
                    .amount-words {
                        flex: 1; padding: 12px 16px;
                        background: #f8fafc; border-radius: 8px;
                        border: 1px dashed #cbd5e1;
                    }
                    .amount-words .aw-label {
                        font-size: 8px; font-weight: 700; letter-spacing: 1.5px;
                        text-transform: uppercase; color: #64748b; margin-bottom: 6px;
                    }
                    .amount-words .aw-value {
                        font-size: 10px; font-style: italic; font-weight: 600;
                        color: #1a3c5e; line-height: 1.5; text-transform: capitalize;
                    }
                    .totals-box {
                        width: 320px; flex-shrink: 0;
                        border-radius: 8px; overflow: hidden;
                        border: 1px solid #e2e8f0;
                    }
                    .totals-box .row {
                        display: flex; justify-content: space-between; align-items: center;
                        padding: 8px 14px; font-size: 10px;
                        border-bottom: 1px solid #f1f5f9;
                    }
                    .totals-box .row:last-child { border-bottom: none; }
                    .totals-box .row .lbl { color: #64748b; }
                    .totals-box .row .val { font-weight: 600; color: #334155; }
                    .totals-box .row.total-ttc {
                        background: linear-gradient(135deg, #1a3c5e, #295AA7);
                        color: white; padding: 12px 14px;
                    }
                    .totals-box .row.total-ttc .lbl {
                        color: white; font-weight: 700;
                        letter-spacing: 1.5px; font-size: 10px;
                        text-transform: uppercase;
                    }
                    .totals-box .row.total-ttc .val {
                        color: white; font-size: 15px; font-weight: 800;
                    }

                    /* SIGNATURES */
                    .signatures {
                        display: flex; justify-content: space-between; gap: 20px;
                        margin-top: 24px; padding-top: 18px;
                        border-top: 1px solid #e2e8f0;
                    }
                    .signature-box { flex: 1; text-align: center; padding-top: 8px; }
                    .signature-box .sig-title {
                        font-size: 9px; font-weight: 700; letter-spacing: 1.5px;
                        text-transform: uppercase; color: #64748b; margin-bottom: 4px;
                    }
                    .signature-box .sig-hint {
                        font-size: 8px; font-style: italic;
                        color: #94a3b8; margin-bottom: 20px;
                    }
                    .signature-box .sig-area {
                        height: 60px; border-bottom: 1.5px solid #cbd5e1;
                        margin-bottom: 6px;
                    }
                    .signature-box .sig-area.stamp {
                        border-bottom: 1.5px dashed #cbd5e1;
                    }

                    /* FOOTER */
                    .legal-footer {
                        margin-top: 22px; padding-top: 14px;
                        border-top: 2px solid #1a3c5e;
                        display: flex; justify-content: space-between; align-items: center;
                        font-size: 8px; color: #94a3b8; letter-spacing: 0.5px;
                    }
                    .legal-footer .legal-left { display: flex; gap: 16px; flex-wrap: wrap; }
                    .legal-footer .legal-right { text-align: right; font-weight: 600; color: #64748b; }
                    .legal-footer strong { color: #1a3c5e; }

                    /* WATERMARK */
                    .watermark {
                        position: fixed; top: 50%; left: 50%;
                        transform: translate(-50%, -50%) rotate(-35deg);
                        font-size: 100px; font-weight: 900; letter-spacing: 8px;
                        opacity: 0.05; pointer-events: none; z-index: 0;
                        white-space: nowrap; text-transform: uppercase;
                    }
                    .watermark.converted { color: #6366f1; }
                    .watermark.expired   { color: #f97316; }
                    .watermark.refused   { color: #ef4444; }
                    .watermark.accepted  { color: #10b981; }

                    @media print {
                        body { background: white; padding: 0; }
                        .watermark { display: block; }
                    }
                </style>
            </head>
            <body>
                ${devis.statut === 'converti_facture' ? '<div class="watermark converted">CONVERTI</div>' : ''}
                ${estExpirePrint && devis.statut !== 'converti_facture' ? '<div class="watermark expired">EXPIRÉ</div>' : ''}
                ${devis.statut === 'refuse' ? '<div class="watermark refused">REFUSÉ</div>' : ''}
                ${devis.statut === 'accepte' ? '<div class="watermark accepted">ACCEPTÉ</div>' : ''}

                <div class="invoice">

                    <!-- BANDEAU SUPÉRIEUR -->
                    <div class="top-band">
                        <div class="top-band-left">
                            <div class="logo-container">
                                ${logoSrc ? `<img src="${logoSrc}" alt="Logo" />` : `<div class="logo-fallback">${logoText}</div>`}
                            </div>
                            <div class="company-header">
                                <h1>${company.name}</h1>
                                <div class="tagline">Votre partenaire de confiance</div>
                            </div>
                        </div>
                        <div class="top-band-right">
                            <div class="doc-type">Devis</div>
                            <div class="doc-number">N° ${devis.numero_proforma}</div>
                            <div class="doc-date">Émis le ${formatDate(devis.date_proforma)}</div>
                        </div>
                    </div>

                    <!-- BANDEAU STATUT -->
                    <div class="status-strip status-${statut.class}">
                        ${statut.label}
                    </div>

                    <!-- PARTIES -->
                    <div class="parties">
                        <div class="party">
                            <div class="party-label">Émetteur</div>
                            <div class="party-name">${company.name}</div>
                            <div class="party-details">
                                <div><span class="label">Adresse</span>${company.address}</div>
                                <div><span class="label">Téléphone</span>${company.phone}</div>
                                <div><span class="label">Email</span>${company.email}</div>
                                <div><span class="label">Site web</span>${company.site_web}</div>
                            </div>
                        </div>
                        <div class="party party-client">
                            <div class="party-label">Client</div>
                            <div class="party-name">${devis.client_nom || 'Client non spécifié'}</div>
                            <div class="party-details">
                                ${devis.code_client ? `<div><span class="label">Code</span>${devis.code_client}</div>` : ''}
                                ${devis.client_telephone ? `<div><span class="label">Téléphone</span>${devis.client_telephone}</div>` : ''}
                                ${devis.client_email ? `<div><span class="label">Email</span>${devis.client_email}</div>` : ''}
                                ${devis.client_adresse ? `<div><span class="label">Adresse</span>${devis.client_adresse}</div>` : ''}
                            </div>
                        </div>
                    </div>

                    <!-- MÉTADONNÉES -->
                    <div class="invoice-meta">
                        <div class="meta-card">
                            <div class="meta-label">Date d'émission</div>
                            <div class="meta-value">${formatDate(devis.date_proforma)}</div>
                        </div>
                        <div class="meta-card validity ${estExpirePrint ? 'urgent' : ''}">
                            <div class="meta-label">Valable jusqu'au</div>
                            <div class="meta-value">${formatDate(devis.date_validite)}</div>
                        </div>
                    </div>

                    ${devis.notes ? `
                        <div class="notes-box">
                            <strong>Note :</strong> ${devis.notes}
                        </div>
                    ` : ''}

                    <!-- TABLEAU DES LIGNES -->
                    <div class="table-wrapper">
                        <table class="lines">
                            <thead>
                                <tr>
                                    <th style="width: 12%;">Référence</th>
                                    <th style="width: 38%;">Désignation</th>
                                    <th class="center" style="width: 8%;">Qté</th>
                                    <th class="right" style="width: 14%;">Prix Unit. HT</th>
                                    <th class="center" style="width: 10%;">Remise</th>
                                    <th class="right" style="width: 18%;">Total HT</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${devis.lignes?.map(ligne => `
                                    <tr>
                                        <td class="ref">${ligne.reference || '-'}</td>
                                        <td class="designation">
                                            ${ligne.designation || ''}
                                            ${ligne.description ? `<div style="font-size: 8.5px; color: #94a3b8; font-weight: 400; margin-top: 2px;">${ligne.description}</div>` : ''}
                                        </td>
                                        <td class="center">${ligne.quantite || 0}</td>
                                        <td class="right">${formatCurrency(ligne.prix_unitaire_ht || 0)}</td>
                                        <td class="center">
                                            ${ligne.remise ? `<span class="remise-tag">${ligne.remise}%</span>` : '<span style="color: #cbd5e1;">—</span>'}
                                        </td>
                                        <td class="right total">${formatCurrency(ligne.montant_ligne_ht || 0)}</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                    </div>

                    <!-- TOTAUX + MONTANT EN LETTRES -->
                    <div class="totals-section">
                        <div class="amount-words">
                            <div class="aw-label">Arrêté à la somme de</div>
                            <div class="aw-value">${montantEnLettres.toLowerCase()} francs CFA</div>
                        </div>

                        <div class="totals-box">
                            <div class="row">
                                <span class="lbl">Total HT</span>
                                <span class="val">${formatCurrency(totalHT)}</span>
                            </div>
                            ${devis.remise_globale > 0 ? `
                            <div class="row">
                                <span class="lbl">Remise globale</span>
                                <span class="val" style="color: #10b981;">− ${formatCurrency(devis.remise_globale)}</span>
                            </div>
                            ` : ''}
                            ${taxes.tva_active && totalTVA > 0 ? `
                            <div class="row">
                                <span class="lbl">TVA (${taxes.tva_taux}%)</span>
                                <span class="val">${formatCurrency(totalTVA)}</span>
                            </div>
                            ` : ''}
                            ${taxes.css_active && totalCSS > 0 ? `
                            <div class="row">
                                <span class="lbl">CSS (${taxes.css_taux}%)</span>
                                <span class="val">${formatCurrency(totalCSS)}</span>
                            </div>
                            ` : ''}

                            <div class="row total-ttc">
                                <span class="lbl">Total TTC</span>
                                <span class="val">${formatCurrency(totalTTC)}</span>
                            </div>
                        </div>
                    </div>

                    <!-- SIGNATURES -->
                    <div class="signatures">
                        <div class="signature-box">
                            <div class="sig-title">Signature Client</div>
                            <div class="sig-hint">Précédée de la mention « Lu et approuvé »</div>
                            <div class="sig-area"></div>
                        </div>
                        <div class="signature-box">
                            <div class="sig-title">Cachet & Signature</div>
                            <div class="sig-hint">${company.name}</div>
                            <div class="sig-area stamp"></div>
                        </div>
                    </div>

                    <!-- FOOTER LÉGAL -->
                    <div class="legal-footer">
                        <div class="legal-left">
                            ${company.rc ? `<span><strong>RC</strong> ${company.rc}</span>` : ''}
                            ${company.nif ? `<span><strong>NIF</strong> ${company.nif}</span>` : ''}
                            ${company.patente ? `<span><strong>Patente</strong> ${company.patente}</span>` : ''}
                        </div>
                        <div class="legal-right">
                            Document généré le ${new Date().toLocaleDateString('fr-FR')} — ${company.name}
                        </div>
                    </div>

                </div>
            </body>
            </html>
        `;
    };

    const handlePrint = () => {
        const htmlContent = genererHTMLDevis();
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();
        iframe.contentWindow.onload = () => {
            iframe.contentWindow.print();
            setTimeout(() => {
                if (document.body.contains(iframe)) document.body.removeChild(iframe);
            }, 1000);
        };
        showToast('info', 'Impression', "Document envoyé à l'impression");
    };

    const handleGeneratePDF = async () => {
        setGeneratingPDF(true);
        try {
            const element = document.createElement('div');
            element.innerHTML = genererHTMLDevis();
            document.body.appendChild(element);
            const opt = {
                margin: [0.5, 0.5, 0.5, 0.5],
                filename: `Devis_${devis.numero_proforma}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, logging: false },
                jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
            };
            await html2pdf().set(opt).from(element).save();
            document.body.removeChild(element);
            showToast('success', 'PDF généré', 'Document généré avec succès');
        } catch (error) {
            console.error("Erreur PDF:", error);
            showToast('error', 'Erreur PDF', error.message);
        } finally {
            setGeneratingPDF(false);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon:        { gradient: 'linear-gradient(135deg, #64748b, #475569)', label: 'Brouillon',           icon: 'ri-draft-line' },
            envoye:           { gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)', label: 'Envoyé',              icon: 'ri-mail-send-line' },
            accepte:          { gradient: 'linear-gradient(135deg, #10b981, #059669)', label: 'Accepté',             icon: 'ri-checkbox-circle-line' },
            refuse:           { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', label: 'Refusé',              icon: 'ri-close-circle-line' },
            converti_facture: { gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)', label: 'Converti en facture',  icon: 'ri-file-transfer-line' },
            expire:           { gradient: 'linear-gradient(135deg, #f97316, #ea580c)', label: 'Expiré',              icon: 'ri-timer-flash-line' }
        };

        const estExpireBadge = statut === 'envoye' && devis && new Date(devis.date_validite) < new Date();
        const config = estExpireBadge ? configs.expire : (configs[statut] || configs.brouillon);

        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    /* ============================================================
       BOUTON ACTION (icône seule)
       ============================================================ */
    const ActionButton = ({ icon, onClick, tooltip, disabled = false, loading = false, color }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <Button
                onClick={onClick}
                disabled={disabled || loading}
                className="action-btn-charte"
                style={{ '--btn-color': color || COLORS.primary }}
            >
                {loading ? <Spinner size="sm" /> : <i className={icon}></i>}
            </Button>
        </OverlayTrigger>
    );

    const estExpire = devis && new Date(devis.date_validite) < new Date();

    /* ============================================================
       ÉTAT : CHARGEMENT
       ============================================================ */
    if (loading && !devis) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-file-copy-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement du devis
                        </h5>
                        <p className="text-muted small">Récupération des informations en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !devis) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon">
                                <i className="ri-error-warning-line"></i>
                            </div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error || "Devis non trouvé"}</p>
                            </div>
                            <Button onClick={() => navigate('/ventes/devisListe')} className="btn-modern-outline">
                                <i className="ri-arrow-left-line me-1"></i>Retour
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }} ref={printRef}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-file-copy-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Détail du devis</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-barcode-line"></i>
                                        {devis.numero_proforma}
                                    </span>
                                    <span className="badge-modern">
                                        <i className="ri-calendar-line"></i>
                                        {formatDate(devis.date_proforma)}
                                    </span>
                                    <span className="badge-modern">
                                        <i className="ri-user-line"></i>
                                        {devis.client_nom}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {getStatutBadge(devis.statut)}
                            <div className="d-flex gap-2">
                                <ActionButton icon="ri-arrow-left-line" onClick={() => navigate('/ventes/devisListe')} tooltip="Retour" />
                                <ActionButton icon="ri-printer-line" onClick={handlePrint} tooltip="Imprimer" />
                                <ActionButton icon="ri-file-pdf-line" onClick={handleGeneratePDF} tooltip="PDF" loading={generatingPDF} disabled={generatingPDF} color={COLORS.danger} />
                            </div>
                        </div>
                    </div>

                    {/* ===== CARTE RÉSUMÉ ===== */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="p-4">
                            <Row className="align-items-center g-3">
                                <Col md={3}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                            <i className="ri-barcode-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">N° Devis</span>
                                            <div className="stat-value-charte" style={{ color: COLORS.primary }}>{devis.numero_proforma}</div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={2}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                            <i className="ri-calendar-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">Date</span>
                                            <div className="stat-value-charte">{formatDate(devis.date_proforma)}</div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={2}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                            <i className="ri-time-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">Validité</span>
                                            <div className="stat-value-charte" style={{ color: estExpire ? COLORS.danger : '#1e293b' }}>
                                                {formatDate(devis.date_validite)}
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={2}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                            <i className="ri-money-euro-circle-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">Total TTC</span>
                                            <div className="stat-value-charte" style={{ color: COLORS.primary, fontWeight: 700 }}>
                                                {formatCurrency(devis.net_a_payer)}
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="text-end">
                                        <div className="reste-box-charte" style={{
                                            background: estExpire ? `${COLORS.danger}10` : `${COLORS.success}10`,
                                            border: `1px solid ${estExpire ? COLORS.danger : COLORS.success}25`
                                        }}>
                                            <span className="small text-muted d-block">Statut</span>
                                            <span className={`fw-bold ${estExpire ? 'text-danger' : 'text-success'}`} style={{ fontSize: '1.1rem' }}>
                                                {estExpire ? 'Expiré' : (devis.statut === 'accepte' ? 'Prêt à convertir' : 'En cours')}
                                            </span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== INFOS SOCIÉTÉ & CLIENT ===== */}
                    <Row className="g-3 mb-4">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100 card-hover-charte">
                                <Card.Header className="card-section-header">
                                    <div className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                        <i className="ri-building-line"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Informations société</h6>
                                </Card.Header>
                                <Card.Body>
                                    <h5 className="fw-bold mb-3" style={{ color: COLORS.primary }}>{company.name}</h5>
                                    <p className="info-line-charte"><i className="ri-map-pin-line"></i>{company.address}</p>
                                    <p className="info-line-charte"><i className="ri-phone-line"></i>{company.phone}</p>
                                    <p className="info-line-charte"><i className="ri-mail-line"></i>{company.email}</p>
                                    <p className="info-line-charte"><i className="ri-global-line"></i>{company.site_web}</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100 card-hover-charte">
                                <Card.Header className="card-section-header">
                                    <div className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-user-line"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Informations client</h6>
                                </Card.Header>
                                <Card.Body>
                                    <h5 className="fw-bold mb-3">{devis.client_nom}</h5>
                                    <p className="info-line-charte"><i className="ri-barcode-line"></i>Code: <strong>{devis.code_client}</strong></p>
                                    {devis.client_telephone && <p className="info-line-charte"><i className="ri-phone-line"></i>{devis.client_telephone}</p>}
                                    {devis.client_email && <p className="info-line-charte"><i className="ri-mail-line"></i>{devis.client_email}</p>}
                                    {devis.client_adresse && <p className="info-line-charte"><i className="ri-map-pin-line"></i>{devis.client_adresse}</p>}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ===== NOTES ===== */}
                    {devis.notes && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4" style={{ borderLeft: `4px solid ${COLORS.warning}` }}>
                            <Card.Body className="py-3 d-flex align-items-center gap-2">
                                <i className="ri-file-text-line fs-5" style={{ color: COLORS.warning }}></i>
                                <span className="fw-semibold">Note :</span>
                                <span className="text-muted">{devis.notes}</span>
                            </Card.Body>
                        </Card>
                    )}

                    {/* ===== TABLEAU PRODUITS ===== */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Header className="card-section-header justify-content-between">
                            <div className="d-flex align-items-center gap-2">
                                <div className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                    <i className="ri-shopping-cart-line"></i>
                                </div>
                                <h6 className="mb-0 fw-semibold">Détail des produits</h6>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <span className="badge-modern">
                                    <i className="ri-stack-line"></i>
                                    {devis.lignes?.length || 0} article(s)
                                </span>
                                <span className="badge-modern" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                    <i className="ri-calculator-line"></i>
                                    {devis.lignes?.reduce((acc, l) => acc + (l.quantite || 0), 0)} unités
                                </span>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table className="mb-0 align-middle table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4" style={{ width: '15%' }}>Référence</th>
                                            <th style={{ width: '40%' }}>Désignation</th>
                                            <th className="text-center" style={{ width: '10%' }}>Qté</th>
                                            <th className="text-end" style={{ width: '15%' }}>Prix HT</th>
                                            <th className="text-center" style={{ width: '10%' }}>Remise</th>
                                            <th className="text-end pe-4" style={{ width: '15%' }}>Total HT</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {devis.lignes?.map((ligne, index) => (
                                            <tr key={index}>
                                                <td className="ps-4">
                                                    <span className="reference-badge-charte">
                                                        <i className="ri-barcode-line"></i>{ligne.reference}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{ligne.designation}</div>
                                                    {ligne.description && (
                                                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>{ligne.description}</small>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <span className="quantity-badge-charte">{formatNumber(ligne.quantite)}</span>
                                                </td>
                                                <td className="text-end">{formatCurrency(ligne.prix_unitaire_ht)}</td>
                                                <td className="text-center">
                                                    {ligne.remise ? (
                                                        <span className="remise-badge-charte">
                                                            <i className="ri-discount-percent-line"></i>{ligne.remise}%
                                                        </span>
                                                    ) : <span className="text-muted">-</span>}
                                                </td>
                                                <td className="text-end pe-4 fw-bold" style={{ color: COLORS.primary }}>
                                                    {formatCurrency(ligne.montant_ligne_ht)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== RÉCAP + ACTIONS ===== */}
                    <Row className="g-3">
                        <Col md={7}>
                            <Card className="border-0 shadow-sm rounded-3 h-100 card-hover-charte">
                                <Card.Header className="card-section-header">
                                    <div className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-calculator-line"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Récapitulatif financier</h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className="recap-line-charte">
                                        <span>Total HT</span>
                                        <span className="fw-bold">{formatCurrency(devis.montant_ht)}</span>
                                    </div>
                                    {devis.remise_globale > 0 && (
                                        <div className="recap-line-charte text-success">
                                            <span><i className="ri-discount-percent-line me-1"></i>Remise globale</span>
                                            <span className="fw-semibold">-{formatCurrency(devis.remise_globale)}</span>
                                        </div>
                                    )}
                                    {taxes.tva_active && devis.montant_tva > 0 && (
                                        <div className="recap-line-charte">
                                            <span>TVA ({taxes.tva_taux}%)</span>
                                            <span className="fw-semibold" style={{ color: COLORS.warning }}>+{formatCurrency(devis.montant_tva)}</span>
                                        </div>
                                    )}
                                    {taxes.css_active && devis.montant_css > 0 && (
                                        <div className="recap-line-charte">
                                            <span>CSS ({taxes.css_taux}%)</span>
                                            <span className="fw-semibold" style={{ color: COLORS.info }}>+{formatCurrency(devis.montant_css)}</span>
                                        </div>
                                    )}

                                    <div className="recap-total-charte">
                                        <span className="fw-bold">TOTAL TTC</span>
                                        <span className="fw-bold" style={{ color: COLORS.primary, fontSize: '1.5rem' }}>
                                            {formatCurrency(devis.net_a_payer)}
                                        </span>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={5}>
                            <Card className="border-0 shadow-sm rounded-3 h-100 card-hover-charte">
                                <Card.Header className="card-section-header">
                                    <div className="section-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                        <i className="ri-flashlight-line"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Actions rapides</h6>
                                </Card.Header>
                                <Card.Body className="d-flex flex-column justify-content-center">
                                    <div className="d-grid gap-3">
                                        {devis.statut === 'accepte' && (
                                            <Button
                                                onClick={() => setShowConvertModal(true)}
                                                className="btn-modern-success py-3"
                                            >
                                                <i className="ri-file-transfer-line me-2"></i>
                                                Convertir en facture
                                            </Button>
                                        )}
                                        {devis.statut === 'brouillon' && (
                                            <>
                                                <Button
                                                    onClick={() => navigate(`/ventes/devis/modifier/${devis.id_proforma}`)}
                                                    className="btn-modern-primary py-3"
                                                >
                                                    <i className="ri-pencil-line me-2"></i>
                                                    Modifier le devis
                                                </Button>
                                                <Button
                                                    onClick={() => navigate(`/ventes/devis/envoyer/${devis.id_proforma}`)}
                                                    className="btn-modern-warning py-3"
                                                >
                                                    <i className="ri-mail-send-line me-2"></i>
                                                    Envoyer le devis
                                                </Button>
                                            </>
                                        )}
                                        {devis.statut === 'converti_facture' && (
                                            <div className="text-center py-3 rounded-3" style={{ background: `${COLORS.success}10`, color: COLORS.success }}>
                                                <i className="ri-checkbox-circle-line me-1"></i>
                                                Ce devis a été converti en facture
                                            </div>
                                        )}
                                        {estExpire && devis.statut === 'envoye' && (
                                            <div className="text-center py-3 rounded-3" style={{ background: `${COLORS.warning}10`, color: COLORS.warning }}>
                                                <i className="ri-timer-flash-line me-1"></i>
                                                Ce devis a expiré le {formatDate(devis.date_validite)}
                                            </div>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL CONVERSION ===== */}
            <Modal show={showConvertModal} onHide={() => setShowConvertModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.success}, #059669)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon">
                            <i className="ri-file-transfer-line"></i>
                        </div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Convertir en facture</Modal.Title>
                            <p className="text-white-50 mb-0 small">Cette action est irréversible</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4 text-center">
                    <p className="text-muted mb-4">
                        Êtes-vous sûr de vouloir convertir ce devis en facture ?
                    </p>
                    <Alert className="alert-info-charte text-start mb-0">
                        <div className="d-flex align-items-start gap-3">
                            <i className="ri-information-line fs-4"></i>
                            <div>
                                <small className="d-block">✓ Le devis sera marqué comme « Converti »</small>
                                <small className="d-block">✓ Une nouvelle facture sera créée automatiquement</small>
                                <small className="d-block">✓ Cette opération ne peut pas être annulée</small>
                            </div>
                        </div>
                    </Alert>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowConvertModal(false)} className="btn-modern-outline">Annuler</Button>
                    <Button
                        onClick={handleConvertirEnFacture}
                        disabled={saving}
                        className="btn-modern-success px-4"
                    >
                        {saving ? (
                            <><Spinner size="sm" className="me-2" />Conversion...</>
                        ) : (
                            <><i className="ri-check-line me-2"></i>Confirmer</>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white;
                    padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .status-badge-charte {
                    color: white; padding: 0.4rem 1.2rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 0.5rem;
                }

                /* ===== BOUTON ACTION ===== */
                .action-btn-charte {
                    width: 42px; height: 42px; border-radius: 12px;
                    display: inline-flex; align-items: center; justify-content: center;
                    border: 1.5px solid #e2e8f0; background: white;
                    color: #64748b; font-size: 1.1rem;
                    transition: all 0.2s ease;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== CARTE STATS ===== */
                .stat-item-charte { display: flex; align-items: center; gap: 0.75rem; }
                .stat-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .stat-label-charte {
                    display: block; font-size: 0.7rem; font-weight: 500;
                    text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8;
                }
                .stat-value-charte { font-size: 1.05rem; font-weight: 600; color: #1e293b; }
                .reste-box-charte {
                    padding: 0.75rem 1.25rem; border-radius: 12px;
                    text-align: right; display: inline-block;
                }

                /* ===== SECTIONS DE CARTE ===== */
                .card-section-header {
                    background: transparent; border: none;
                    padding: 1.25rem 1.5rem 0.75rem;
                    display: flex; align-items: center; gap: 0.75rem;
                }
                .section-icon-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.1rem;
                }
                .card-hover-charte { transition: all 0.3s ease; }
                .card-hover-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 32px rgba(0,0,0,0.08) !important;
                }

                /* ===== INFO LINE ===== */
                .info-line-charte {
                    display: flex; align-items: center; gap: 0.6rem;
                    font-size: 0.85rem; color: #475569; margin-bottom: 0.6rem;
                }
                .info-line-charte i { color: var(--secondary); font-size: 1rem; }

                /* ===== TABLE CHARTE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.85rem 0.75rem; vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .quantity-badge-charte {
                    display: inline-flex; align-items: center; justify-content: center;
                    background: #e2e8f0; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.8rem;
                    font-weight: 600; min-width: 32px;
                }
                .remise-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #fef3c7; color: #92400e;
                    padding: 0.15rem 0.5rem; border-radius: 6px;
                    font-size: 0.7rem; font-weight: 500;
                }

                /* ===== RÉCAP ===== */
                .recap-line-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 0.5rem 0; border-bottom: 1px dashed #e2e8f0;
                    font-size: 0.85rem; color: #475569;
                }
                .recap-line-charte:last-of-type { border-bottom: none; }
                .recap-total-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 0.85rem 1rem;
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    border-radius: 12px;
                    margin-top: 0.5rem;
                    border: 1px solid #e2e8f0;
                }

                /* ===== BOUTONS MODERNES ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary, .btn-modern-success, .btn-modern-warning {
                    border: none; color: white; padding: 0.6rem 2rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.9rem;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; justify-content: center;
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-warning {
                    background: linear-gradient(135deg, var(--warning), #d97706);
                    box-shadow: 0 4px 12px rgba(245,158,11,0.3);
                }
                .btn-modern-warning:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(245,158,11,0.4); color: white;
                }

                /* ===== ALERTES ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }
                .alert-info-charte {
                    border: none; background: #eff6ff;
                    border-left: 4px solid var(--info);
                    border-radius: 12px; padding: 1rem 1.25rem;
                    font-size: 0.85rem; color: #1e40af;
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .stat-item-charte { margin-bottom: 0.75rem; }
                    .reste-box-charte { width: 100%; text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                    .recap-total-charte span:last-child { font-size: 1.2rem !important; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}